import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { demoDevicesData } from '../data/demoDevicesData';

const InteractiveDemoSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeDevice, setActiveDevice] = useState(null);
  const [hoveredDevice, setHoveredDevice] = useState(null);

  return (
    <section
      id="interactive-demo"
      ref={ref}
      className="section-padding bg-gradient-to-b from-[#0f1640] to-[#0a0e27] relative overflow-hidden"
      data-testid="interactive-demo-section"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-64 h-64 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <div className="inline-block glass px-6 py-2 rounded-full mb-6">
            <span className="text-[#00d4ff] font-semibold uppercase tracking-wider text-sm">
              {t('interactiveDemo.badge')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('interactiveDemo.title')}
            <span className="gradient-text block mt-2">{t('interactiveDemo.subtitle')}</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            {t('interactiveDemo.description')}
          </p>
        </motion.div>

        {/* Professional Animated Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {demoDevicesData.map((device, index) => {
            const isActive = activeDevice === device.id;
            const isHovered = hoveredDevice === device.id;

            return (
              <motion.div
                key={device.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ 
                  y: -12,
                  scale: 1.03,
                  transition: { type: "spring", stiffness: 300 }
                }}
                onClick={() => setActiveDevice(isActive ? null : device.id)}
                onMouseEnter={() => setHoveredDevice(device.id)}
                onMouseLeave={() => setHoveredDevice(null)}
                className="cursor-pointer relative"
              >
                <motion.div 
                  className="glass-strong rounded-3xl p-8 h-full relative overflow-hidden"
                  animate={{
                    boxShadow: isActive 
                      ? `0 0 40px ${device.color}60, 0 0 80px ${device.color}30`
                      : isHovered
                      ? `0 0 20px ${device.color}40`
                      : '0 0 0px transparent'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Animated Background Pattern */}
                  <AnimatePresence>
                    {(isActive || isHovered) && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 pointer-events-none"
                      >
                        {/* Particle Effect */}
                        {[...Array(8)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-2 h-2 rounded-full"
                            style={{ 
                              backgroundColor: device.color,
                              left: `${20 + (i * 10)}%`,
                              top: `${30 + (i % 3) * 20}%`
                            }}
                            animate={{
                              y: [0, -20, 0],
                              opacity: [0.3, 0.7, 0.3],
                              scale: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              delay: i * 0.2,
                            }}
                          />
                        ))}
                        
                        {/* Glow Ring Effect */}
                        <motion.div
                          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full"
                          style={{ 
                            border: `2px solid ${device.color}`,
                            opacity: 0.3
                          }}
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 0, 0.3],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Icon with Advanced Animation */}
                  <motion.div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 relative z-10"
                    style={{ backgroundColor: `${device.color}20` }}
                    animate={{
                      scale: isActive ? 1.15 : isHovered ? 1.08 : 1,
                      rotate: isActive ? [0, 5, -5, 0] : 0,
                    }}
                    transition={{ 
                      scale: { type: "spring", stiffness: 300 },
                      rotate: { duration: 0.5 }
                    }}
                  >
                    {/* Icon Glow */}
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 rounded-2xl"
                        style={{ backgroundColor: device.color }}
                        animate={{ opacity: [0, 0.3, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                    )}
                    
                    <motion.div
                      animate={{
                        rotate: isActive ? 360 : 0,
                        scale: isHovered ? 1.1 : 1
                      }}
                      transition={{ 
                        rotate: { duration: 2, repeat: isActive ? Infinity : 0, ease: "linear" },
                        scale: { duration: 0.2 }
                      }}
                    >
                      <device.icon 
                        className="text-4xl relative z-10" 
                        style={{ color: device.color }}
                      />
                    </motion.div>
                  </motion.div>

                  {/* Content */}
                  <motion.h3 
                    className="text-xl font-bold text-white mb-3 relative z-10"
                    animate={{
                      color: isActive ? device.color : '#ffffff'
                    }}
                  >
                    {t(device.titleKey)}
                  </motion.h3>
                  
                  <motion.p
                    className="text-white/70 text-sm leading-relaxed relative z-10"
                    animate={{
                      opacity: isActive ? 1 : 0.7
                    }}
                  >
                    {t(device.descKey)}
                  </motion.p>

                  {/* Active Indicator */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="mt-4 flex items-center space-x-2 relative z-10"
                      >
                        <motion.div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: device.color }}
                          animate={{ 
                            scale: [1, 1.3, 1],
                            opacity: [1, 0.5, 1]
                          }}
                          transition={{ 
                            duration: 1.5,
                            repeat: Infinity
                          }}
                        />
                        <span className="text-xs text-white/80 font-semibold">
                          {t('interactiveDemo.active')}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Border Gradient Animation */}
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 rounded-3xl pointer-events-none"
                      style={{
                        background: `linear-gradient(45deg, ${device.color}40, transparent, ${device.color}40)`,
                      }}
                      animate={{
                        backgroundPosition: ['0% 0%', '100% 100%'],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatType: 'reverse',
                      }}
                    />
                  )}
                </motion.div>
              </motion.div>
            );
          })}

          {/* CTA Card with Animation */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8 }}
            whileHover={{ y: -8 }}
            className="glass-strong rounded-3xl p-8 flex flex-col justify-center items-center text-center bg-gradient-to-br from-[#00d4ff]/10 to-[#0099ff]/10 relative overflow-hidden"
          >
            {/* Animated Background */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background: [
                  'radial-gradient(circle at 20% 50%, rgba(0,212,255,0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 80% 50%, rgba(0,212,255,0.1) 0%, transparent 50%)',
                  'radial-gradient(circle at 20% 50%, rgba(0,212,255,0.1) 0%, transparent 50%)',
                ]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
              }}
            />
            
            <motion.div 
              className="text-5xl mb-4 relative z-10"
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
              }}
            >
              ✨
            </motion.div>
            <h3 className="text-xl font-bold text-white mb-3 relative z-10">
              {t('interactiveDemo.clickToExplore')}
            </h3>
            <p className="text-white/60 text-sm relative z-10">
              {t('interactiveDemo.tapInstruction')}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default InteractiveDemoSection;
